import { useSettings } from '@backpackjs/storefront';
import { useVariantPrices } from '../../hooks';

import { ProductStars, Svg } from '../../snippets';


export function ProductHeader({ 
  isMobile, 
  isMobileViewport, 
  product,
  isGiftCard, 
  selectedVariant,
  productTitleMobilePlacement,
  smoothScrollTo,
  note,
  headerExtraClasses,
 }) {
  const { price, compareAtPrice } = useVariantPrices({
    variant: selectedVariant,
  });
  const priceNumber = Number(price?.replace('$', ''));
  const compareAtPriceNumber = Number(compareAtPrice?.replace('$', ''));
  const discountPercentage = ((compareAtPriceNumber - priceNumber) / compareAtPriceNumber) * 100;
  
  const settings = useSettings();
  const { enabledProductType, enabledColorName, enabledShopPayInstallments, finalSaleText } = { ...settings?.product?.details };
  const { enabledStarRating } = { ...settings?.product?.reviews };

  const isVisibleHeader =
    (isMobile && isMobileViewport) || (!isMobile && !isMobileViewport);

  const headerClasses = productTitleMobilePlacement === 'aboveImage'
  ? isMobile ? 'col-span-12 md:hidden' : 'max-md:hidden'
  : headerExtraClasses;

  const productTitle = product.title.split(' | ')[0];
  const hasNote = note !== undefined && note !== null;
  const isFinalSale = product.tags.includes('Final Sale');
  const installmentsPrice = (priceNumber / 4).toFixed(2);
  const installmentsMessage = priceNumber < 50 ? `Pay in 4 interest-free installments for orders over $50.00 with` : priceNumber < 150 ? `Pay in 4 interest-free installments of $${installmentsPrice} with` : `4 interest-free installments, or from $${installmentsPrice}/mo with`;

  return (
    <div className={headerClasses}>
      {enabledStarRating && (
        <ProductStars
          productId={product?.legacyResourceId}
        />
      )}

      {/* ensure only one H1 is within the DOM at a time */}
      {/* remove ternary and only use <h1> if only one header placement is used */}
      {isVisibleHeader ? (
        <h1 className="text-title-h1 normal-case">{productTitle}</h1>
      ) : (
        <h1 className="text-title-h2 normal-case">{productTitle}</h1>
      )}

      {enabledProductType && (
        <h2 className="text-title-h5">{product.productType}</h2>
      )}

      {enabledColorName && product.optionsMap?.Color && (
        <h2 className="text-title-h6">
          {selectedVariant?.selectedOptionsMap?.Color}
        </h2>
      )}

      {compareAtPrice ? (
        <>
          <div className="mt-0 flex items-baseline gap-1 md:mt-2.5">
            <p
              className="text-title-h4 leading-none text-secondary"
              aria-label={`Current price ${price}`}
            >
              {price}
            </p>
            <p
              className="text-xl text-mediumGray line-through"
              aria-label={`Original price ${compareAtPrice}`}
            >
              {compareAtPrice}
            </p>
          </div>
          <p className="text-title-h6 text-secondary">
            {discountPercentage.toFixed(0)}% off
            <span className="sr-only">
              {` (${discountPercentage.toFixed(0)}% discount applied)`}
            </span>
          </p>
        </>
      ) : (
        <div className="mt-0 flex gap-1 md:mt-2.5">
          <p
            className="text-title-h4 leading-none"
            aria-label={`Price ${price}`}
          >
            {price}
          </p>
        </div>
      )}
      {enabledShopPayInstallments && !isGiftCard && (
        <p className="text-sm" id="shopify-installments">
          <span id="shopify-installments-content" className="flex">
            {installmentsMessage}
            <Svg
              className="ml-1 w-[59px]"
              role="img"
              src={`/svgs/noprecache/icons/ShopPay.svg#ShopPay`}
              alt="Shop Pay"
              title="Shop Pay"
              aria-label="Shop Pay"
              viewBox="0 0 683 164"
            />
          </span>
        </p>
      )}

      {isFinalSale && <p className="text-secondary">{finalSaleText}</p>}
      {hasNote && <p className="text-body text-mediumGray">{note?.value}</p>}
    </div>
  );
}

ProductHeader.displayName = 'ProductHeader';
